import React, { useState, useEffect, useRef } from "react"
import { Select, Input, Button, Flex } from "antd"
import { SearchOutlined } from "@ant-design/icons"
import { useTranslation } from "react-i18next"
import LabeledField from "pagesV2/client/Profile/Tabs/ProfileInfo/LabeledField"
import styles from "./index.module.css"

const CategorySelect = ({ categoryOptions, selectedCategoryId, categoryManagement, onCategoryChange, form }) => {
  const { t } = useTranslation()
  const { search, setSearch, showAddForm, setShowAddForm, name, setName, error, setError, isLoading, reset, handleAdd, handleClearError } =
    categoryManagement
  const selectRef = useRef(null)
  const isMounted = useRef(true)
  const [pendingCategoryName, setPendingCategoryName] = useState(null)
  const [dropdownOpen, setDropdownOpen] = useState(false)

  useEffect(() => {
    return () => {
      isMounted.current = false
    }
  }, [])

  useEffect(() => {
    if (!pendingCategoryName) return
    const match = categoryOptions?.find((cat) => cat.label?.toLowerCase() === pendingCategoryName.toLowerCase())
    if (match) {
      form?.setFieldsValue({ category: match.value })
      setDropdownOpen(false)
      setPendingCategoryName(null)
    }
  }, [categoryOptions, pendingCategoryName, form])

  const handleAddCategory = async () => {
    const categoryName = name.trim()
    try {
      await handleAdd()
      if (categoryName && isMounted.current) setPendingCategoryName(categoryName)
    } catch (error) {
      // Error already set in hook
    }
  }

  return (
    <LabeledField
      labelKey={t("equipment.category")}
      name="category"
      rules={[
        {
          required: true,
          message: t("equipment.category_required")
        }
      ]}
    >
      <Select
        ref={selectRef}
        allowClear
        showSearch={false}
        placeholder={t("equipment.select_category")}
        defaultValue={selectedCategoryId}
        className={styles.categorySelect}
        open={dropdownOpen}
        onDropdownVisibleChange={(open) => {
          setDropdownOpen(open)
          if (!open) reset()
        }}
        dropdownRender={(menu) => (
          <>
            <div className={styles.inlineAddCategory}>
              {!showAddForm ? (
                <Button type="link" onClick={() => setShowAddForm(true)} className={styles.addCategoryLink}>
                  + {t("equipment.add_category")}
                </Button>
              ) : (
                <Flex vertical gap={8}>
                  <Input
                    autoFocus
                    placeholder={t("equipment.category_name")}
                    value={name}
                    status={error ? "error" : ""}
                    onChange={(e) => {
                      setName(e.target.value)
                      handleClearError()
                    }}
                    onPressEnter={handleAddCategory}
                  />
                  {error && <div className={styles.addCategoryError}>{error}</div>}
                  <Flex gap={8} className={styles.addCategoryActions}>
                    <Button onClick={reset}>{t("equipment.cancel")}</Button>
                    <Button type="primary" loading={isLoading} onClick={handleAddCategory}>
                      {t("equipment.save")}
                    </Button>
                  </Flex>
                </Flex>
              )}
            </div>
            <div className={styles.searchCategory}>
              <Input
                placeholder={t("equipment.search_category")}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                prefix={<SearchOutlined className={styles.categorySearchIcon} />}
                allowClear
                className={styles.categorySearchInput}
              />
            </div>

            {menu}
          </>
        )}
        optionFilterProp="children"
        filterOption={false}
        onChange={(value) => {
          if (value === undefined) {
            form?.setFieldsValue({ category: undefined })
            return
          }
          form?.setFieldsValue({ category: value })
          onCategoryChange?.(value)
          setDropdownOpen(false)
        }}
      >
        {Array.isArray(categoryOptions) &&
          categoryOptions.length > 0 &&
          categoryOptions
            .filter((cat) => !search || cat.label?.toLowerCase().includes(search.toLowerCase()))
            .map((cat) => (
              <Select.Option className={styles.categoryOption} key={cat.value} value={cat.value}>
                {cat.label}
              </Select.Option>
            ))}
      </Select>
    </LabeledField>
  )
}

export default CategorySelect
